import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CartesianGrid,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { CustomSelect } from 'components/ui/CustomSelect'

export interface MatchingCategoryAverages {
  languages: number
  hard_skills: number
  soft_skills: number
  nice_to_have: number
  niveau_etude: number
  main_missions: number
  certifications: number
  experience_years: number
}

export interface RadarOfferOption {
  id: number
  label: string
}

export interface CandidateScorePoint {
  application_id: number | null
  candidate_id: number
  candidate_name: string
  cv_matching_score: number
  interview_score: number
}

type SkillsRadarChartVariant = 'both' | 'radar' | 'scatter'

interface SkillsRadarChartProps {
  data: MatchingCategoryAverages | null
  totalResults?: number
  scatterData?: CandidateScorePoint[]
  scatterTotal?: number
  footerLabel?: string
  offers?: RadarOfferOption[]
  selectedOfferId?: number | null
  onOfferChange?: (offerId: number | null) => void
  variant?: SkillsRadarChartVariant
}

const CATEGORY_KEYS: Array<keyof MatchingCategoryAverages> = [
  'languages',
  'hard_skills',
  'soft_skills',
  'nice_to_have',
  'niveau_etude',
  'main_missions',
  'certifications',
  'experience_years',
]

const CATEGORY_I18N_KEYS: Record<keyof MatchingCategoryAverages, string> = {
  languages: 'matchings.categories.languages',
  hard_skills: 'matchings.categories.technicalSkills',
  soft_skills: 'matchings.categories.softSkills',
  nice_to_have: 'matchings.categories.niceToHave',
  niveau_etude: 'matchings.categories.education',
  main_missions: 'matchings.categories.mainMissions',
  certifications: 'matchings.categories.certifications',
  experience_years: 'matchings.categories.professionalExperience',
}

function RadarChartPanel({
  chartData,
  hasRadarData,
  t,
}: {
  chartData: Array<{ key: string; category: string; value: number; fullMark: number }>
  hasRadarData: boolean
  t: any
}) {
  if (!hasRadarData) {
    return (
      <div className="flex h-full min-h-[380px] flex-1 items-center justify-center text-sm text-muted-foreground">
        {t('dashboard.recruitmentStats.noMatchingData', 'Aucune donnée de matching pour cette période')}
      </div>
    )
  }

  return (
    <div className="h-full min-h-[380px] w-full flex-1">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart cx="50%" cy="50%" outerRadius="82%" data={chartData}>
          <PolarGrid stroke="#D4D4D4" />
          <PolarAngleAxis
            dataKey="category"
            tick={{ fill: '#666666', fontSize: 11 }}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={{ fill: '#8A8A8A', fontSize: 10 }}
            tickCount={6}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || !payload[0]) return null
              const point = payload[0].payload as (typeof chartData)[number]

              return (
                <div className="rounded-[8px] border border-border bg-card px-4 py-3 text-card-foreground shadow-lg">
                  <p className="mb-1 text-sm font-semibold">{point.category}</p>
                  <p className="text-xs font-semibold text-[#0284C7]">
                    {t('dashboard.recruitmentStats.averageScore', 'Moyenne')}: {point.value.toFixed(1)}%
                  </p>
                </div>
              )
            }}
          />
          <Radar
            name={t('dashboard.recruitmentStats.averageScore', 'Moyenne')}
            dataKey="value"
            stroke="#0EA5E9"
            fill="#0EA5E9"
            fillOpacity={0.28}
            strokeWidth={2}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  )
}

function ScatterChartPanel({
  scatterData,
  hasScatterData,
  t,
}: {
  scatterData: CandidateScorePoint[]
  hasScatterData: boolean
  t: any
}) {
  if (!hasScatterData) {
    return (
      <div className="flex h-[390px] items-center justify-center text-sm text-muted-foreground">
        {t('dashboard.recruitmentStats.noScoreDistributionData', 'Aucune donnée de scores disponible')}
      </div>
    )
  }

  return (
    <div className="h-[390px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="4 4" stroke="#D4D4D4" />
          <XAxis
            type="number"
            dataKey="cv_matching_score"
            name={t('dashboard.recruitmentStats.cvMatchingScore', 'Score matching CV')}
            domain={[0, 100]}
            tickLine={false}
            axisLine={{ stroke: '#8A8A8A' }}
            tick={{ fill: '#666666', fontSize: 11 }}
            label={{
              value: t('dashboard.recruitmentStats.cvMatchingScore', 'Score matching CV'),
              position: 'insideBottom',
              offset: -4,
              fill: '#666666',
              fontSize: 12,
            }}
          />
          <YAxis
            type="number"
            dataKey="interview_score"
            name={t('dashboard.recruitmentStats.interviewScore', 'Score entretien')}
            domain={[0, 100]}
            tickLine={false}
            axisLine={{ stroke: '#8A8A8A' }}
            tick={{ fill: '#666666', fontSize: 11 }}
            label={{
              value: t('dashboard.recruitmentStats.interviewScore', 'Score entretien'),
              angle: -90,
              position: 'insideLeft',
              fill: '#666666',
              fontSize: 12,
            }}
          />
          <ZAxis range={[48, 48]} />
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            content={({ active, payload }) => {
              if (!active || !payload || !payload[0]) return null
              const point = payload[0].payload as CandidateScorePoint

              return (
                <div className="rounded-[8px] border border-border bg-card px-4 py-3 text-card-foreground shadow-lg">
                  <p className="mb-2 text-sm font-semibold">{point.candidate_name}</p>
                  <div className="space-y-1 text-xs">
                    <p className="font-semibold text-[#0284C7]">
                      {t('dashboard.recruitmentStats.cvMatchingScore', 'Score matching CV')}: {point.cv_matching_score.toFixed(1)}%
                    </p>
                    <p className="font-semibold text-[#0284C7]">
                      {t('dashboard.recruitmentStats.interviewScore', 'Score entretien')}: {point.interview_score.toFixed(1)}%
                    </p>
                  </div>
                </div>
              )
            }}
          />
          <Scatter
            name={t('dashboard.recruitmentStats.candidateDistributionTitle', 'Distribution des candidats')}
            data={scatterData}
            fill="#0EA5E9"
            fillOpacity={0.65}
          />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  )
}

export function SkillsRadarChart({
  data,
  totalResults = 0,
  scatterData = [],
  scatterTotal = 0,
  footerLabel,
  offers = [],
  selectedOfferId = null,
  onOfferChange,
  variant = 'both',
}: SkillsRadarChartProps) {
  const { t } = useTranslation()

  const offerOptions = useMemo(() => [
    {
      value: 'all',
      label: t('dashboard.recruitmentStats.allOffers', 'Toutes les offres'),
    },
    ...offers.map((offer) => ({
      value: String(offer.id),
      label: offer.label,
    })),
  ], [offers, t])

  const chartData = useMemo(() => {
    if (!data) return []

    return CATEGORY_KEYS.map((key) => ({
      key,
      category: t(CATEGORY_I18N_KEYS[key]),
      value: data[key] ?? 0,
      fullMark: 100,
    }))
  }, [data, t])

  const hasRadarData = totalResults > 0 || chartData.some((item) => item.value > 0)
  const hasScatterData = scatterTotal > 0 || scatterData.length > 0

  const showRadar = variant === 'both' || variant === 'radar'

  const cardTitle = variant === 'scatter'
    ? t('dashboard.recruitmentStats.candidateDistributionTitle', 'Distribution des candidats')
    : t('dashboard.recruitmentStats.skillsRadarTitle', 'Moyenne des scores par section')

  return (
    <Card className="flex h-full w-full max-w-full flex-col rounded-[6px]">
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <CardTitle className="text-lg font-semibold tracking-normal text-foreground">
              {cardTitle}
            </CardTitle>
            {footerLabel && (
              <p className="text-xs text-muted-foreground">{footerLabel}</p>
            )}
          </div>
          {onOfferChange && (showRadar || variant === 'scatter') && (
            <CustomSelect
              className="w-full lg:w-[320px] lg:shrink-0"
              options={offerOptions}
              value={selectedOfferId === null ? 'all' : String(selectedOfferId)}
              onChange={(value) => onOfferChange(value === 'all' ? null : Number(value))}
              placeholder={t('dashboard.recruitmentStats.allOffers', 'Toutes les offres')}
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col">
        {variant === 'both' ? (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="min-w-0">
              <RadarChartPanel chartData={chartData} hasRadarData={hasRadarData} t={t} />
            </div>
            <div className="min-w-0">
              <p className="mb-3 text-sm font-medium text-foreground">
                {t('dashboard.recruitmentStats.candidateDistributionTitle', 'Distribution des candidats')}
              </p>
              <ScatterChartPanel scatterData={scatterData} hasScatterData={hasScatterData} t={t} />
            </div>
          </div>
        ) : showRadar ? (
          <RadarChartPanel chartData={chartData} hasRadarData={hasRadarData} t={t} />
        ) : (
          <ScatterChartPanel scatterData={scatterData} hasScatterData={hasScatterData} t={t} />
        )}
      </CardContent>
    </Card>
  )
}
